// React & React Router
import React, { useEffect } from "react";
import { useRouteMatch, useLocation } from "react-router-dom";

// Material-UI Core & Styles
import { CircularProgress, Typography, Box } from "@material-ui/core";

// Material-UI Icons & Components
import { CloudUpload, Create, DoneAll } from "@material-ui/icons";

// Sample Instructions Data
import {
  research_report_instructions,
  white_paper_instructions,
  blog_instructions,
  newsletter_instructions,
  key_insights_instructions,
  case_study_instructions,
  handout_instructions,
  project_plan_instructions,
} from "../Data/sampleInstructions.js";

// Custom & Local Components
import NotificationContext from "views/Components/Context.js";
import CreateCourseCard from "views/Components/CreateCourseCard";
import CreateWritingModal from "views/Components/CreateWritingModal.js";

// API Utilities
import {
  fetchAllWritingsAPI,
  createWritingAPI,
  deleteWritingAPI,
  fetchWritingTemplatesAPI,
  updateWritingTagsAPI,
  cloneEntryAPI,
  inviteUserAPI,
} from "views/APIs/APIUtility";
import AssetDashboard from "views/Components/AssetDashboard.js";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import WritingModel from "../Models/WritingModel.js";
import { image_name, identifiers } from "../Constants.js";
import { fetchImageAsFile } from "views/Dashboard/Course/CourseConstants";

const useStyles = makeStyles({
  ...styles,
});

const instructions_mapping = {
  research_report: research_report_instructions,
  white_paper: white_paper_instructions,
  project_plan: project_plan_instructions,
  blog: blog_instructions,
  news_letter: newsletter_instructions,
  case_study: case_study_instructions,
  key_insights: key_insights_instructions,
  handout: handout_instructions,
};

export default function WritingsBoard() {
  // Access to global notifications
  const { showNotification } = React.useContext(NotificationContext);
  // Get the current route path and initialize current dialog text
  const path = useRouteMatch().path;
  // Utility & Resource Setup
  const images = require.context("assets/img/create_cards_16_9/", true);
  const location = useLocation();
  const classes = useStyles();

  // State Variables
  // Alert and Modal state management
  const [createWritingModal, setCreateWritingModal] = React.useState(false);

  const [identifier, setIdentifier] = React.useState("research_report");
  const [identifierPrint, setIdentifierPrint] = React.useState(
    identifiers["research_report"]
  );
  // New Writing Details with default outline template and empty fields
  const [newWritingDetails, setNewWritingDetails] = React.useState(
    new WritingModel()
  );

  // Other UI and data states
  const [iconImage, setIconImage] = React.useState();
  const [allWritings, setAllWritings] = React.useState([]);
  const [initialWritings, setInitialWritings] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });
  const [metapromptChecked, setMetaPromptChecked] = React.useState(false);

  // const [writingCreationLoader, setWritingCreationLoader] = React.useState(
  //   false
  // );
  const [writingCreationLoader, setWritingCreationLoader] = React.useState({
    isLoading: false,
    message: "",
  });
  // Writing ID state (note: TODO; remove writing_id later)
  const [writingId, setWritingId] = React.useState("");
  const [currentDialog, setCurrentDialog] = React.useState("Create Writing");
  const [writingAdded, setWritingAdded] = React.useState(false);

  // Event Handlers and Utility Functions

  // Handler: Upload icon image, saving the file to state
  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIconImage(file);
    }
  };

  // Handler: Delete a writing entry using API call
  const handleDeleteWriting = async (writingData) => {
    const formData = new FormData();
    formData.append("writing_id", writingData._id);

    try {
      await deleteWritingAPI(formData);
      // Remove deleted writing from state
      setAllWritings(
        allWritings.filter((writing) => writing._id !== writingData._id)
      );
      showNotification("Writing deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleCloneCourse = async (courseData) => {
    try {
      setLoading({
        loading: true,
        message: "Cloning " + identifierPrint + "...",
      });
      const formData = new FormData();
      formData.append("collection", "writing_design");
      formData.append("id", courseData._id);

      const data = await cloneEntryAPI(formData);
      setAllWritings((prevCourses) => [
        ...prevCourses,
        _mapResourceToAppType(data),
      ]);
      setWritingAdded(true);
      showNotification(`${identifierPrint} cloned successfully`, "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  // Fetch writings from the API and filter by identifier
  useEffect(() => {
    const fetchWritings = async () => {
      try {
        setLoading({
          loading: true,
          message: "Loading " + identifierPrint + "...",
        });
        let identifier_local = "";
        let identifierPrint_local = "";
        if (location.pathname) {
          identifier_local = location.pathname.split("/")[2];
          identifierPrint_local = identifiers[identifier_local];
          setIdentifier(identifier_local);
          setIdentifierPrint(identifierPrint_local);
        }
        let data = await fetchAllWritingsAPI();
        // Filter writings based on the current identifier
        data = data.filter(
          (writing) => writing.identifier === identifier_local
        );
        const mappedData = data.map(_mapResourceToAppType);
        setAllWritings(mappedData);
        setInitialWritings(mappedData);
      } catch (error) {
        showNotification(error.message || "An error occurred", "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchWritings();
  }, [identifier]);

  // Handler: Create a new writing entry using API call
  const handleCreateWriting = async () => {
    // setWritingCreationLoader({
    //   isLoading: true,
    //   message: "Creating " + identifierPrint + " ...",
    // });
    // Use uploaded icon image or fetch default image if none is provided
    const imageToUse =
      iconImage || (await fetchImageAsFile(images(image_name[identifier])));

    const formData = new FormData();
    formData.append("writing_id", writingId);
    formData.append("writing_name", newWritingDetails.name);
    formData.append("writing_description", newWritingDetails.description);
    formData.append("writing_image", imageToUse);
    formData.append("writing_outline", newWritingDetails.outline);
    formData.append("identifier", identifier);
    // Append any additional files
    newWritingDetails.uploadedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const data = await createWritingAPI(formData);
      window.open(`/create/${identifier}/${data._id}`, "_blank");
      setAllWritings([...allWritings, _mapResourceToAppType(data)]);
      setWritingAdded(true);
      showNotification(identifierPrint + " created successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      // setWritingCreationLoader({ isLoading: false, message: "" });
      handleCreateWritingModalClose();
    }
  };

  const handleSaveLabel = async (data) => {
    try {
      const formData = new FormData();
      formData.append("writing_id", data._id);
      if (data.tags.length === 0) {
        formData.append("tags", []);
      }
      // Append each tag separately
      else {
        data.tags.forEach((tag) => formData.append("tags", tag));
      }
      await updateWritingTagsAPI(formData);
      setAllWritings((prevWriting) =>
        prevWriting.map((writing) =>
          writing._id === data._id ? { ...writing, tags: data.tags } : writing
        )
      );
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const requestData = {
        collection_type: "writing",
        project_id: data._id,
        invitee_email: data.email,
      };

      const response = await inviteUserAPI(requestData);

      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allWritings.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };

      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allCourses state
      const updatedWritings = allWritings.map((writing) =>
        writing._id === updatedProjectId ? newAsset : writing
      );

      setAllWritings(updatedWritings);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  const handleGenerateTemplate = async (prompt, use_metaprompt) => {
    try {
      setWritingCreationLoader({
        isLoading: true,
        message: `Let’s create some templates for the ${identifierPrint}...`,
      });
      const formData = new FormData();
      formData.append("identifier", identifier);
      // Append any additional files
      newWritingDetails.uploadedFiles.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("target_audience", newWritingDetails.target_audience);
      formData.append("expected_length", newWritingDetails.expected_length);
      formData.append("tone", newWritingDetails.tone);

      formData.append("prompt", prompt);
      formData.append("use_metaprompt", use_metaprompt);
      const data = await fetchWritingTemplatesAPI(formData);
      const formattedTemplates = (data.templates || []).map((item) => ({
        template_name: (item.template_name || "Unnamed")
          .replace(/\s*template\s*$/i, "")
          .trim(),
        template_content: item.template_content || "No content available",
      }));

      // Update state with the new template data
      setNewWritingDetails((prevState) => ({
        ...prevState,
        template: formattedTemplates,
      }));
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setWritingCreationLoader({ isLoading: false, message: "" });
    }
  };

  // Handler: Close create writing modal and reset new writing details
  const handleCreateWritingModalClose = () => {
    setWritingCreationLoader({ isLoading: false, message: "" });
    setCreateWritingModal(false);
    setCurrentDialog("Create Writing");
    setNewWritingDetails(new WritingModel());
  };

  // Display Helper: Return status icon based on the writing's current status
  const getStatusIcon = (status) => {
    switch (status) {
      case "In Design Phase":
        return <Create style={{ color: "#435966" }} />;
      case "In Review Phase":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      default:
        return null;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude course_name and course_description
    const {
      writing_name,
      writing_description,
      writing_image,
      ...rest
    } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: writing_name, // Add name property
      description: writing_description, // Add description property
      image: writing_image, // Add image property
    };
  };

  // Conditional Rendering: Show loading spinner if writings data is still loading
  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreateWritingModal
        identifier={identifier}
        identifierPrint={identifierPrint}
        newWritingDetails={newWritingDetails}
        setNewWritingDetails={setNewWritingDetails}
        writingId={writingId}
        setWritingId={setWritingId}
        createWritingModal={createWritingModal}
        handleCreateWritingModalClose={handleCreateWritingModalClose}
        handleCreateWriting={handleCreateWriting}
        handleIconUpload={handleIconUpload}
        iconImage={iconImage}
        writingCreationLoader={writingCreationLoader}
        setWritingCreationLoader={setWritingCreationLoader}
        currentDialog={currentDialog}
        setCurrentDialog={setCurrentDialog}
        handleGenerateTemplate={handleGenerateTemplate}
      />

      <AssetDashboard
        path={path}
        welcomeTitle={`${identifierPrint} Dashboard`}
        welcomeSubtitle={`Start by creating a new ${identifierPrint.toLowerCase()} or reviewing your existing ${identifierPrint.toLowerCase()}.`}
        allAssets={allWritings}
        setAllAssets={setAllWritings}
        initialAssets={initialWritings}
        noAssetMessage={`Create or Review your ${identifierPrint.toLowerCase()} to get started.`}
        linkPath={`/`}
        state={{ identifier: identifier }}
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateWritingModal}
        createAssetButtonText={`Create ${identifierPrint}`}
        handleSaveLabel={handleSaveLabel}
        handleInviteSend={handleInviteSend}
        asset_identifier={identifierPrint.toLowerCase()}
        handleDeleteAsset={handleDeleteWriting}
        handleCloneAsset={handleCloneCourse}
        assetAdded={writingAdded}
        setAssetAdded={setWritingAdded}
        setInitialAssets={setInitialWritings}
        workflowConfig={{
          type: "writing",
          title: `${identifierPrint} Workflow`,
          description: `Create high-quality written artifacts such as blogs, case studies, project plans, research reports, handouts, or key insight briefs.`,
          steps: [
            {
              label: "1. Specify details",
              body:
                "Define the detailed name, description, target audience, output " +
                "length, and the tone or style.",
            },
            {
              label: "2. Upload resources",
              body:
                "Add background documents, references, or supporting materials.",
            },
            {
              label: "3. Choose a template",
              body:
                "Select from a library of templates that best fits your artifact’s purpose.",
            },
            {
              label: "4. Finalize draft",
              body:
                "Make edits, adjust templates if needed, " +
                "and then download or export the final version for use.",
            },
          ],
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/writings-demo.mp4",
          examples: [
            {
              title: "Case Study: The Collapse of Silicon Valley Bank",
              description:
                "Silicon Valley Bank specialized in the tech startup industry but was not prepared for a bank run as companies started burning more cash.",
              link:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qu-writing-design/687c318eebbe08acedffb42b/pre_processed_deliverables/687e60c9ebbe08acedffb5f9.pdf",
              mediaUrl:
                "https://media.npr.org/assets/img/2023/03/14/gettyimages-1248236117-42761965a59d07f029c7b3e55e88fafb3733fd05.jpg?s=800&c=85&f=webp",
              mediaAlt: "Case Study: The Collapse of Silicon Valley Bank",
            },
            {
              title:
                "Research Report: Army Aviation's Role in Unified Land Operations",
              description:
                "This research report examines the pivotal role of Army Aviation in Unified Land Operations. The report outlines the integration of aviation capabilities into land operations and how recent technological advancements enhance operational effectiveness.",
              link:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qu-writing-design/673e6f87f29afcaf3c57fe46/pre_processed_deliverables/67a22f6bb4fd8049f84993a2.pdf",
              mediaUrl:
                "https://upload.wikimedia.org/wikipedia/commons/0/0e/CSA-2006-02-24-095553.jpg",
              mediaAlt:
                "Research Report: Army Aviation's Role in Unified Land Operations",
            },
            {
              title:
                "Research Report: Bond Convexity and Duration Impacts on Portfolio Performance",
              description:
                "This research report examines the impacts of bond convexity and duration on portfolio performance. Using quantitative metrics and real-world examples, we analyze how these factors influence bond price changes and inform risk adjustments, providing finance professionals with actionable insights.",
              link:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qu-writing-design/6838b40dd043abbfa6299e42/pre_processed_deliverables/6894ba192642dcc3463f25cd.pdf",
              mediaUrl:
                "https://www.investopedia.com/thmb/yLY3z0gEa3Ib5F9d9S5rq2bDBQQ=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/DurationandConvexitytoMeasureBondRisk2-0429456c85984ad3b220cd23a760cda5.png",
              mediaAlt:
                "Research Report: Bond Convexity and Duration Impacts on Portfolio Performance",
            },
          ],
        }}
      />
    </div>
  );
}
